import React from 'react'

import i18n from 'live/services/i18n'

import { ClassicPagination, IClassicPaginationProps } from '../ClassicPagination/Pagination'
import { ILivePaginationProps, LivePagination } from '../LivePagination/Pagination'
import { CustomTable } from './styles'
import { truncate } from '../../utils/truncate'

// eslint-disable-next-line @typescript-eslint/ban-types
type IListItem = object

type IDirection = 'ASC' | 'DESC'

interface IProps<TList extends IListItem> {
    columns: IColumns<TList>[]
    /** Here where your list is used Ps.: columns must have listRef that is a reference to item on the list  for sort work */
    list: TList[]
    /** For custom css */
    className?: string
    /** If you dont like default order icon you can use other one here from Font Awesome */
    orderIcon?: string
    /** The field that you are sorting stay outside component so when your list need to refresh just pass the new sort here */
    sortField: string // keyof TList
    /** Sort initial direction */
    sortDirection: IDirection
    /** This is only present in fields that are sortable and return 2 params the sortList and the sortedBy atribute */
    onSortClick: (newSortBy: keyof TList, direction?: IDirection, newList?: TList[]) => void
    /** Here you can use any icon of font awsome with a callback for your component */
    configOptions?: { icon: string; onClickConfig: (k: TList) => void; hidden?: (k: TList) => boolean }[]
    /** If you need pagination PS.: if you pass on 1 item in array dropdown of perpage will not render because it doesnt neet to render */
    pagination?: IClassicPaginationProps | ILivePaginationProps
    /** If you need adicional table props */
    tableProps?: Omit<React.TableHTMLAttributes<HTMLTableElement>, 'className'>
    /** Label to show on the pagination */
    label?: string
}

interface IColumns<TList extends IListItem> {
    title: string
    listRef: keyof TList
    sortable?: boolean
    type?: 'default' | 'icon'
}

export function CustomList<T extends IListItem>({
    columns,
    list,
    className,
    orderIcon = 'fa fa-long-arrow-',
    sortField,
    sortDirection,
    onSortClick = () => null,
    configOptions,
    pagination,
    tableProps,
    label
}: IProps<T>): JSX.Element {
    const onSort = (listRef: keyof T): void => {
        if (sortField !== listRef) {
            onSortClick(listRef, 'ASC')
        } else {
            onSortClick(listRef, sortDirection === 'ASC' ? 'DESC' : 'ASC')
        }
    }
    ;(pagination as ILivePaginationProps).label = label

    return (
        <>
            <CustomTable className={className ? `custom-list-${className}` : 'custom-list'} {...tableProps}>
                <thead>
                    <tr>
                        {columns.map((column, index) => (
                            <th
                                key={`custom-list-header-row-1-column-${index}`}
                                className={`custom-list-column-${column.listRef} custom-list-column-ref-list`}
                                style={{ cursor: column?.sortable && 'pointer' }}
                                onClick={() => column?.sortable && onSort(column.listRef)}
                            >
                                {i18n(column.title)}
                                {column?.sortable && (
                                    <>
                                        {' '}
                                        <i
                                            className={`${orderIcon + (sortDirection === 'DESC' ? 'down' : 'up')}  `}
                                            style={{
                                                visibility: column.listRef === sortField ? 'visible' : 'hidden'
                                            }}
                                        />
                                    </>
                                )}
                            </th>
                        ))}
                        {configOptions && <th className="custom-list-column-actions" />}
                    </tr>
                </thead>
                <tbody>
                    {list.map((listItem, lineIndex) => (
                        <tr key={`custom-list-body-row-${lineIndex}`} className={`custom-list-body-row-${lineIndex}`}>
                            {columns.map((column, columnIndex) =>
                                column.type === undefined || column.type === 'default' || column.type === null ? (
                                    <td
                                        key={`custom-list-body-row-${lineIndex}-col-${columnIndex}`}
                                        className={`custom-list-column-${column.listRef}`}
                                    >
                                        {(typeof listItem[column.listRef] === 'string' ||
                                            typeof listItem[column.listRef] === 'number' ||
                                            React.isValidElement(listItem[column.listRef])) &&
                                            truncate(listItem[column.listRef], 100)}
                                    </td>
                                ) : (
                                    <td
                                        key={`custom-list-body-row-${lineIndex}-col-${columnIndex}`}
                                        className={`custom-list-column-${column.listRef}`}
                                    >
                                        {
                                            <i
                                                style={{ pointerEvents: 'none', textAlign: 'center' }}
                                                key={`custom-list-body-row-${lineIndex}-col-${columnIndex}`}
                                                className={JSON.parse(JSON.stringify(listItem[column.listRef])) ?? ''}
                                            />
                                        }
                                    </td>
                                )
                            )}
                            {configOptions && (
                                <td className="custom-list-column-actions">
                                    {configOptions.map((icon, i) => (
                                        <>
                                            {(icon.hidden == undefined || !icon.hidden(listItem)) && (
                                                <i
                                                    key={i}
                                                    className={icon.icon}
                                                    onClick={() => icon.onClickConfig(listItem)}
                                                />
                                            )}
                                        </>
                                    ))}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </CustomTable>
            {pagination && pagination.type === 'classic' && <ClassicPagination {...pagination} />}
            {pagination && pagination.type === 'live' && <LivePagination {...pagination} />}
        </>
    )
}
